import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

// PATCH 36 Me18 — live favicon badge + tab title. For admins we poll the real pending count
// (verification queue + open abuse reports) and paint a badge on the favicon plus a meaningful
// title. For everyone else the tab shows a stable, meaningful "Kadana" title.
const BASE_TITLE = "Kadana — compliance-ready software";

function paintFavicon(count) {
  try {
    const size = 64;
    const canvas = document.createElement("canvas");
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext("2d");
    // Light tile so the dark-grey "K." reads clearly.
    ctx.fillStyle = "#FAFAFA";
    if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(2, 2, size - 4, size - 4, 12); ctx.fill(); }
    else ctx.fillRect(0, 0, size, size);
    // "K" in dark grey, followed by a full stop in the accent colour.
    ctx.font = "700 40px Inter, Arial, sans-serif";
    ctx.textBaseline = "alphabetic"; ctx.textAlign = "left";
    const kx = 12, ky = 47;
    ctx.fillStyle = "#3f3f46"; ctx.fillText("K", kx, ky);
    const kw = ctx.measureText("K").width;
    ctx.fillStyle = "#9adee0"; ctx.fillText(".", kx + kw + 1, ky);
    if (count > 0) {
      ctx.fillStyle = "#9adee0";
      ctx.beginPath(); ctx.arc(size - 15, 15, 15, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#18181B";
      ctx.font = "700 22px Inter, Arial, sans-serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText(count > 99 ? "99+" : String(count), size - 15, 16);
    }
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
    link.href = canvas.toDataURL("image/png");
  } catch { /* favicon painting is best-effort */ }
}

export default function usePendingBadge() {
  const { user } = useAuth();
  const location = useLocation();
  const timer = useRef(null);
  useEffect(() => {
    // The admin verification-queue count only ever appears on admin screens. Every other surface
    // (workspace, project, billing, public pages) always shows the plain end-user title, even when
    // the logged-in account happens to be an admin — no admin-only count leaks into an end-user tab.
    const onAdminRoute = location.pathname.startsWith("/admin");
    if (!user || user.platform_role !== "admin" || !onAdminRoute) {
      document.title = BASE_TITLE; paintFavicon(0); return undefined;
    }
    const pull = () => api.get("/polish/pending-count").then((r) => {
      const n = r.data?.total || 0;
      document.title = n > 0 ? `(${n}) Kadana admin — ${n} to review` : "Kadana admin";
      paintFavicon(n);
    }).catch(() => {});
    pull();
    timer.current = setInterval(pull, 60000);
    return () => clearInterval(timer.current);
  }, [user, location.pathname]);
}
